/**
 * A property search panel that allows users to query property data using different search types.
 *
 * The `PropertySearchSection` component provides a flexible UI for selecting a search type,
 * entering query values, and executing property lookups. It handles user input, result fetching,
 * loading state, and feedback messages for both success and error cases.
 *
 * It integrates with parent components via callbacks (`onSearch`, `onResults`, `onSelectResult`)
 * and supports external control through an optional `AccordionSectionHandle` ref.
 * 
 * -- Search.json in the config folder --
 * 
 */

import React, { useEffect, useMemo, useState } from "react";
import AccordionSection, { AccordionSectionHandle } from "../../utils/accordion/AccordionSection";
import { SearchResult, SearchJsonType } from "../../types/config";
import Message from "../modals/MessageModal";

type Props = {
  searchTypes: SearchJsonType[];
  onSearch: (searchType: SearchJsonType, values: Record<string, string>) => Promise<SearchResult[]> | SearchResult[];
  onSelectResult?: (r: SearchResult) => void;
  onResults?: (results: SearchResult[]) => void;
  defaultOpen?: boolean;
  title?: string;
  searchByLabel?: string;
  searchButtonLabel?: string;
  defaultTypeIndex?: number;
  sectionRef?: React.Ref<AccordionSectionHandle>;
};

const PropertySearchSection: React.FC<Props> = (props) => {
  const {
    searchTypes, onSearch, onSelectResult, onResults,
    defaultOpen, title = "Property Search", searchByLabel = "Search By",
    searchButtonLabel = "Search", defaultTypeIndex = 0, sectionRef
  } = props;

  const modeOptions = useMemo(
    () => searchTypes.map((t, i) => ({ value: String(i), label: t.title })),
    [searchTypes]
  );

  const [mode, setMode] = useState<string>(String(Math.min(defaultTypeIndex, Math.max(0, searchTypes.length - 1))));
  const activeType = useMemo(() => searchTypes[Number(mode)] ?? searchTypes[0], [mode, searchTypes]);

  const initialValues = useMemo(() => {
    const acc: Record<string, string> = {};
    (activeType?.fields || []).forEach(f => { acc[f.name] = ""; });
    return acc;
  }, [activeType]);

  const [values, setValues] = useState<Record<string, string>>(initialValues);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<{ variant: "info" | "success" | "warning" | "error"; title: string; body?: string } | null>(null);

  useEffect(() => { setValues(initialValues); setMsg(null); }, [initialValues]);

  const updateField = (k: string, v: string) => setValues(prev => ({ ...prev, [k]: v }));

  const handleSearch = async () => {
    setMsg(null);
    setLoading(true);
    try {
      const res = await onSearch(activeType, values);
      onResults?.(res);

      if (res.length === 1) {
        onSelectResult?.(res[0]);
      } else if (res.length === 0) {
        setMsg({
          variant: "error",
          title: "Error",
          body: "No properties found with that criteria.",
        });
      } else {
        return;
      }
    } catch (e) {
      setMsg({
        variant: "error",
        title: "Search failed.",
        body: e instanceof Error ? e.message : "Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const fieldCount = activeType?.fields?.length ?? 0;

  return (
    <AccordionSection ref={sectionRef} title={title} defaultOpen={defaultOpen}>
      <label className="psa-label">{searchByLabel}</label>
      <select className="psa-select" value={mode} onChange={e => setMode(e.target.value)}>
        {modeOptions.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
      </select>

      <div className="psa-row" style={{ gridTemplateColumns: `repeat(${Math.max(fieldCount, 1)}, 1fr) auto` }}>
        {(activeType?.fields || []).map(f => (
          <input
            key={f.name}
            className="psa-input"
            type={f.type ?? "text"}
            aria-label={f.label ?? f.name}
            placeholder={f.placeholder}
            value={values[f.name] ?? ""}
            onChange={e => updateField(f.name, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (!loading) handleSearch();
              }
            }}
          />
        ))}
        <button className="psa-btn psa-btn--primary" onClick={handleSearch} disabled={loading}>
          {searchButtonLabel}
        </button>
      </div>

      {msg && (
        <Message
          inline={false}
          variant={msg.variant}
          title={msg.title}
          onClose={() => setMsg(null)}
          actions={
            msg.variant === "error"
              ? [{ label: "OK", primary: true, onClick: () => setMsg(null) }]
              : undefined
          }
          autoDismissMs={msg.variant === "info" || msg.variant === "success" ? 4000 : undefined}
        >
          {msg.body}
        </Message>
      )}
    </AccordionSection>
  );
};

export default PropertySearchSection;
